import { AdminLayout } from "components/admin/admin-layout";
import { AdminSearchBar } from "components/admin/admin-search-bar";
import { Button } from "components/buttons/button";
import gql from "graphql-tag";
import { useInput } from "lib/hooks/use-input";
import { parseParam } from "lib/utils";
import { useRouter } from "next/router";
import {
  useGetDiscountCodesQuery,
  useToggleCodeEnabledMutation,
} from "lib/graphql-generated";
import Link from "next/link";
import currency from "currency.js";

const GET_DISCOUNTS = gql`
  query GetDiscountCodes(
    $limit: Int
    $offset: Int
    $conditions: discounts_bool_exp
  ) {
    discounts_aggregate(where: $conditions) {
      aggregate {
        count
      }
    }

    discounts(
      limit: $limit
      offset: $offset
      where: $conditions
      order_by: { created_at: desc }
    ) {
      code
      discount_value
      is_fixed_amount
      created_at
      is_active
      orders_aggregate(where: { status: { _neq: draft } }) {
        aggregate {
          count
        }
      }
    }
  }
`;

const TOGGLE_DISCOUNT = gql`
  mutation ToggleCodeEnabled($code: String!, $isActive: Boolean!) {
    update_discounts_by_pk(
      pk_columns: { code: $code }
      _set: { is_active: $isActive }
    ) {
      code
    }
  }
`;

export const DiscountPage = () => {
  /**
   * Query Strings
   */
  const router = useRouter();
  const pQuery = parseInt(router.query["p"] as string);
  const paginate = Boolean(pQuery) ? pQuery : 0;
  const limit = 25;
  const offset = Boolean(paginate) ? paginate * limit : 0;
  const search = useInput<string>(parseParam(router.query["s"]) || "");

  const { data, loading, error, refetch } = useGetDiscountCodesQuery({
    variables: {
      limit: limit,
      offset: offset,
      conditions: { code: { _ilike: `%${search.initialValue}%` } },
    },
    fetchPolicy: "cache-and-network",
  });

  const [toggleActive] = useToggleCodeEnabledMutation({
    refetchQueries: ["GetDiscountCodes"],
  });

  const handleSearch = (e) => {
    router.replace(`/admin/discounts?p=0&s=${search.value}`);
    search.init(search.value);
  };

  return (
    <AdminLayout
      page='Discounts'
      searchBar={
        <AdminSearchBar
          {...search.bind}
          onSearch={handleSearch}
          placeholder='Search by code'
        />
      }>
      <article className='space-y-4'>
        <header className='flex flex-col xl:flex-row flex-wrap w-full justify-center space-y-4 xl:space-y-0 xl:space-x-4'>
          <h1 className='header text-4xl ml-auto mr-auto xl:ml-0 xl:mr-auto'>
            Discount Codes
          </h1>
          <Button
            buttonType='secondary'
            buttonSize='lg'
            href='/admin/discounts/new'
            className='my-auto ml-auto mr-auto'>
            <svg
              className='w-4 h-4 mr-2 -ml-1 mb-1'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
            <span>New Code</span>
          </Button>
        </header>

        <table className='min-w-full divide-y divide-gray-200 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'>
          <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider'>
            <tr>
              <th className='px-6 py-3'>Code</th>
              <th className='px-6 py-3'>Discount</th>
              <th className='px-6 py-3 hidden xl:table-cell'>Uses</th>
              <th className='px-6 py-3 hidden xl:table-cell'>Date Created</th>
              <th className='px-6 py-3 hidden xl:table-cell'>Actions</th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
            {loading ? (
              <tr className='hover:bg-gray-300'>
                <td
                  className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                  colSpan={4}>
                  Loading Data...
                </td>
              </tr>
            ) : data.discounts.length !== 0 ? (
              data?.discounts.map((discount) => (
                <tr
                  key={discount.code}
                  className={`hover:bg-gray-100 ${
                    discount.is_active ? "" : "bg-yellow-50"
                  }`}>
                  <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                    <Link href={`/admin/discounts/${discount.code}`}>
                      <a className='hover:underline hover:text-green-default'>
                        {discount.code}
                      </a>
                    </Link>
                  </td>
                  <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                    {discount.is_fixed_amount
                      ? currency(discount.discount_value, {
                          fromCents: true,
                          symbol: "£",
                        }).format()
                      : discount.discount_value}
                    {!discount.is_fixed_amount && "%"}
                  </td>
                  <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                    {discount.orders_aggregate.aggregate.count}
                  </td>
                  <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell'>
                    {new Date(discount.created_at).toLocaleDateString("en-GB")}
                  </td>
                  <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell space-x-4'>
                    <button
                      className={`hover:underline ${
                        discount.is_active
                          ? "text-red-default hover:text-red-dark"
                          : "text-green-default hover:text-green-dark"
                      } `}
                      onClick={() => {
                        toggleActive({
                          variables: {
                            code: discount.code,
                            isActive: !discount.is_active,
                          },
                        });
                      }}>
                      {discount.is_active ? "Disable" : "Enable"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr className='hover:bg-gray-300'>
                <td
                  className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                  colSpan={4}>
                  Could not find discounts...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </article>
      {data && (
        <nav
          className='py-3 flex items-center justify-between border-t border-gray-200 '
          aria-label='Pagination'>
          <div className='hidden sm:block'>
            <p className='text-sm text-gray-700'>
              Showing{" "}
              <span className='font-medium'>
                {offset}
                {` `}
              </span>
              to{" "}
              <span className='font-medium'>
                {offset + limit}
                {` `}
              </span>
              of{" "}
              <span className='font-medium'>
                {data?.discounts_aggregate.aggregate.count}
                {` `}
              </span>
              results
            </p>
          </div>
          <div className='flex-1 flex justify-between sm:justify-end'>
            {paginate !== 0 && (
              <Link
                href={`/admin/discounts?p=${paginate - 1}&s=${search.value}`}>
                <a className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                  Previous
                </a>
              </Link>
            )}
            {paginate !==
              Math.floor(data.discounts_aggregate.aggregate.count / 25) && (
              <Link
                href={`/admin/discounts?p=${paginate + 1}&s=${search.value}`}>
                <a className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                  Next
                </a>
              </Link>
            )}
          </div>
        </nav>
      )}
    </AdminLayout>
  );
};

export default DiscountPage;
